import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MathfieldElement } from "mathlive";
import StudentLayout from "../StudentLayout";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

// ✅ Local two-step linear equations (increasing difficulty)
// Last couple include division + brackets
const LOCAL_EQUATION_QUESTIONS = [
  { id: "q1", calculator: "no", latex: "x + 7 = 19", answers: ["12"] },
  { id: "q2", calculator: "no", latex: "x - 9 = 4", answers: ["13"] },
  { id: "q3", calculator: "no", latex: "3x + 5 = 20", answers: ["5"] },
  { id: "q4", calculator: "no", latex: "4x - 6 = 22", answers: ["7"] },

  { id: "q5", calculator: "no", latex: "5x + 3 = -17", answers: ["-4"] },
  { id: "q6", calculator: "no", latex: "-2x + 9 = 1", answers: ["4"] },
  { id: "q7", calculator: "no", latex: "3(x + 7) = 21", answers: ["0"] },
  { id: "q8", calculator: "no", latex: "15 = 5(2x + 11)", answers: ["-4"] },

  { id: "q9", calculator: "yes", latex: "\\frac{x}{3} + 4 = 9", answers: ["15"] },
  { id: "q10", calculator: "yes", latex: "\\frac{2x - 5}{3} = 7", answers: ["13"] },

  // ✅ division + brackets
  { id: "q11", calculator: "yes", latex: "\\frac{3(x - 2)}{4} = 6", answers: ["10"] },
  { id: "q12", calculator: "yes", latex: "5 - \\frac{2(x + 1)}{3} = -1", answers: ["8"] },
];

const STORAGE_KEY = "sle_quiz_progress_v1";

function normalize(s) {
  return String(s ?? "").trim().replace(/\s+/g, "");
}

// Accepts "12", "x=12", "x = 12"
function extractNumericAnswer(raw) {
  const s = normalize(raw);
  // If they typed x=12, grab the RHS
  const match = s.match(/^(x=)?([-+]?\d+(\.\d+)?)$/i);
  if (match) return match[2];
  // If they typed something like x= -4
  const match2 = s.match(/^x=([-+]?\d+(\.\d+)?)$/i);
  if (match2) return match2[1];
  return s; // fallback
}

export default function SolvingLinearEquations() {
  const navigate = useNavigate();

  const questions = useMemo(() => LOCAL_EQUATION_QUESTIONS, []);
  const [current, setCurrent] = useState(0);

  const [studentAnswer, setStudentAnswer] = useState("");
  const [working, setWorking] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const [questionResults, setQuestionResults] = useState(
    Array(questions.length).fill(null)
  );
  const [studentAnswers, setStudentAnswers] = useState(
    Array(questions.length).fill("")
  );
  const [workingAnswers, setWorkingAnswers] = useState(
    Array(questions.length).fill("")
  );

  const [reviewMode, setReviewMode] = useState(false);
  const [done, setDone] = useState(false);

  // Register math-field web component once
  useEffect(() => {
    if (!customElements.get("math-field")) {
      customElements.define("math-field", MathfieldElement);
    }
  }, []);

  // Load saved progress (localStorage)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);

      if (typeof data.current === "number") setCurrent(data.current);
      if (Array.isArray(data.questionResults)) setQuestionResults(data.questionResults);
      if (Array.isArray(data.studentAnswers)) setStudentAnswers(data.studentAnswers);
      if (Array.isArray(data.workingAnswers)) setWorkingAnswers(data.workingAnswers);
      if (typeof data.done === "boolean") setDone(data.done);

      // hydrate current inputs
      const c = typeof data.current === "number" ? data.current : 0;
      setStudentAnswer((data.studentAnswers?.[c] ?? "") + "");
      setWorking((data.workingAnswers?.[c] ?? "") + "");
    } catch {
      // ignore corrupt storage
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save progress (localStorage)
  const saveProgress = (override = {}) => {
    const payload = {
      current,
      questionResults,
      studentAnswers,
      workingAnswers,
      done,
      ...override,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  };

  const updateResult = (result) => {
    const updated = [...questionResults];
    updated[current] = result;
    setQuestionResults(updated);
    return updated;
  };

  const checkAnswer = () => {
    const q = questions[current];
    const correctAnswers = q.answers ?? [];
    const cleaned = extractNumericAnswer(studentAnswer);

    const isCorrect = correctAnswers.some((a) => normalize(a) === normalize(cleaned));
    void playQuizFeedback(isCorrect);

    // persist per-question entries
    const updatedStudentAnswers = [...studentAnswers];
    const updatedWorkingAnswers = [...workingAnswers];
    updatedStudentAnswers[current] = studentAnswer;
    updatedWorkingAnswers[current] = working;
    setStudentAnswers(updatedStudentAnswers);
    setWorkingAnswers(updatedWorkingAnswers);

    if (isCorrect) {
      setFeedback("✅ Correct!");
      const updatedResults = updateResult("correct");
      saveProgress({
        studentAnswers: updatedStudentAnswers,
        workingAnswers: updatedWorkingAnswers,
        questionResults: updatedResults,
      });
      return;
    }

    const newAttempts = attempts + 1;
    setAttempts(newAttempts);

    if (newAttempts >= 3) {
      updateResult("incorrect");
      setFeedback(
        <div>
          {"❌ Not quite. Correct answer: "}
          <BlockMath math={String(correctAnswers[0] ?? "—")} />
          <div style={{ marginTop: 6, opacity: 0.8, fontSize: 14 }}>
            (You can move on now.)
          </div>
        </div>
      );
      saveProgress({
        studentAnswers: updatedStudentAnswers,
        workingAnswers: updatedWorkingAnswers,
      });
    } else {
      setFeedback(`❌ Try again. Attempts left: ${3 - newAttempts}`);
      saveProgress({
        studentAnswers: updatedStudentAnswers,
        workingAnswers: updatedWorkingAnswers,
      });
    }
  };

  const canAdvance = feedback?.toString().startsWith("✅") || attempts >= 3;

  const nextQuestion = () => {
    setFeedback(null);
    setAttempts(0);

    const next = current + 1;

    if (next < questions.length) {
      setCurrent(next);

      // hydrate inputs for next question
      setStudentAnswer(studentAnswers[next] || "");
      setWorking(workingAnswers[next] || "");
      saveProgress({ current: next });
    } else {
      setDone(true);
      saveProgress({ done: true });
    }
  };

  const prevQuestion = () => {
    if (current === 0) return;

    setFeedback(null);
    setAttempts(0);
    const prev = current - 1;
    setCurrent(prev);

    setStudentAnswer(studentAnswers[prev] || "");
    setWorking(workingAnswers[prev] || "");

    saveProgress({ current: prev });
  };

  const handleReview = () => {
    setCurrent(0);
    setReviewMode(true);
    setFeedback(null);
    setAttempts(0);

    setStudentAnswer(studentAnswers[0] || "");
    setWorking(workingAnswers[0] || "");

    saveProgress({ current: 0 });
  };

  const finishReview = () => {
    // You can change this to wherever you want
    navigate("/student-dashboard");
  };

  const score = questionResults.filter((r) => r === "correct").length;

  const feedbackMessage = (score, total) => {
    const percentage = (score / total) * 100;
    if (percentage === 100) return "🌟 Perfect! You got everything right!";
    if (percentage >= 80) return "🎉 Great job! You did really well!";
    if (percentage >= 50) return "👍 Good effort! Keep practising!";
    return "💪 Don't worry! Review your answers and try again!";
  };

  const renderProgress = () => (
    <div style={styles.progressWrapper}>
      {questionResults.map((res, index) => {
        let bgColor = "#636e72";
        if (res === "correct") bgColor = "#00b894";
        if (res === "incorrect") bgColor = "#d63031";

        return (
          <div
            key={index}
            style={{
              ...styles.progressDot,
              backgroundColor: bgColor,
              border: index === current ? "3px solid #f9b710" : "2px solid transparent",
            }}
          >
            <span style={styles.dotNumber}>{index + 1}</span>
          </div>
        );
      })}
    </div>
  );

  const q = questions[current];

  return (
    <StudentLayout>
      <div style={styles.container}>
        <h1 style={styles.heading}>Solving Linear Equations</h1>
        {renderProgress()}

        {reviewMode && (
          <button style={styles.finishReviewBtn} onClick={finishReview}>
            🔙 Finish Review
          </button>
        )}

        {done && !reviewMode ? (
          <div style={styles.resultsBox}>
            <h2 style={{ fontFamily: "Inter, 'Segoe UI', sans-serif", color: "#17324d", fontWeight: 700 }}>
              Score: {score} / {questions.length}
            </h2>
            <p style={styles.feedbackText}>{feedbackMessage(score, questions.length)}</p>
            <button onClick={handleReview} style={styles.reviewBtn}>
              🔍 Review Questions
            </button>
          </div>
        ) : (
          <div style={styles.card}>
            <h3 style={styles.questionHeading}>
              Question {current + 1} of {questions.length}
            </h3>

            <div style={{ marginBottom: 10, display: "flex", justifyContent: "flex-end" }}>
              <CalculatorIcon allowed={q.calculator === "yes"} />
            </div>

            <div style={{ textAlign: "left", lineHeight: "1.6" }}>
              <p>Solve:</p>
              <div className="wrap-math">
                <BlockMath math={q.latex} />
              </div>
            </div>

            <label style={{ fontFamily: "Inter, 'Segoe UI', sans-serif", color: "#334155", fontWeight: 600, marginTop: 10, display: "inline-block" }}>
              Show your working:
            </label>
            <textarea
              rows={4}
              readOnly={reviewMode || questionResults[current] === "correct" || attempts >= 3}
              value={reviewMode ? workingAnswers[current] : working}
              onChange={(e) => !reviewMode && setWorking(e.target.value)}
              placeholder="Show optional working here"
              style={{
                ...styles.workingInput,
                backgroundColor:
                  reviewMode || questionResults[current] === "correct" || attempts >= 3
                    ? "#f0f0f0"
                    : "#fff",
              }}
            />

            <div style={{ margin: "1rem 0" }}>
              <label>
                <strong>Answer:</strong>
              </label>
              <div style={{ display: "flex", alignItems: "center" }}>
                <math-field
                  readOnly={reviewMode || questionResults[current] === "correct" || attempts >= 3}
                  style={{
                    ...styles.mathInput,
                    backgroundColor:
                      reviewMode || questionResults[current] === "correct" || attempts >= 3
                        ? "#f0f0f0"
                        : "#fff",
                  }}
                  value={
                    reviewMode || questionResults[current] === "correct" || attempts >= 3
                      ? studentAnswers[current]
                      : studentAnswer
                  }
                  onInput={(e) => !reviewMode && setStudentAnswer(e.target.value)}
                />
              </div>
            </div>

            {reviewMode && (
              <div style={{ marginBottom: "1rem" }}>
                <p>
                  <strong>
                    {questionResults[current] === "correct"
                      ? "✅ Correct Answer:"
                      : "❌ The correct answer was:"}
                  </strong>
                </p>
                <BlockMath math={String(q.answers?.[0] ?? "—")} />
              </div>
            )}

            {!reviewMode && feedback && (
              <div style={{ marginBottom: "1rem" }}>{feedback}</div>
            )}

            {!reviewMode && !canAdvance && (
              <button onClick={checkAnswer} style={styles.submitBtn}>
                Check Answer
              </button>
            )}

            {(canAdvance || reviewMode) && (
              <>
                {reviewMode && current > 0 && (
                  <button onClick={prevQuestion} style={styles.backBtn}>
                    ← Back
                  </button>
                )}
                <button onClick={nextQuestion} style={styles.nextBtn}>
                  {current + 1 < questions.length ? "Next ➡️" : reviewMode ? "Finish" : "Finish 🎉"}
                </button>
              </>
            )}

            {!reviewMode && current > 0 && (
              <div style={{ marginTop: 10 }}>
                <button onClick={prevQuestion} style={styles.secondaryBtn}>
                  ← Previous
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </StudentLayout>
  );
}

const styles = {
  container: {
    padding: "2rem",
    fontFamily: "'Fredoka', cursive",
    backgroundColor: "#eaf4fc",
    minHeight: "100vh",
  },
  heading: { fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "clamp(1.6rem, 3vw, 2.25rem)", fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.035em", color: "#17324d", textAlign: "center", maxWidth: 760, margin: "0 auto 1.5rem", paddingBottom: "1rem", borderBottom: "3px solid #b8d4e8" },
  questionHeading: { fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "1rem", fontWeight: 600, letterSpacing: "0.025em", color: "#475569", margin: "0 0 1.25rem" },
  card: {
    backgroundColor: "#fff",
    padding: "2rem",
    borderRadius: "12px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    maxWidth: "700px",
    margin: "1rem auto 0",
  },
  mathInput: {
    flex: 1,
    fontSize: "1.2rem",
    padding: "0.5rem",
    borderRadius: "8px",
    border: "1px solid #ccc",
    minWidth: "200px",
    marginLeft: "5px",
    backgroundColor: "#fff",
    transition: "background-color 0.3s ease",
  },
  submitBtn: {
    marginTop: "1rem",
    padding: "0.6rem 1.2rem",
    backgroundColor: "#0984e3",
    color: "white",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    marginRight: "1rem",
  },
  nextBtn: {
    marginTop: "1rem",
    padding: "0.6rem 1.2rem",
    backgroundColor: "#00b894",
    color: "white",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  backBtn: {
    marginTop: "1rem",
    padding: "0.6rem 1.2rem",
    backgroundColor: "#00b894",
    color: "white",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    marginRight: "1rem",
  },
  secondaryBtn: {
    marginTop: "0.25rem",
    padding: "0.45rem 0.9rem",
    backgroundColor: "#636e72",
    color: "white",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  progressWrapper: {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    marginBottom: "1.5rem",
    flexWrap: "wrap",
  },
  progressDot: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "bold",
    fontSize: "1rem",
    color: "white",
    transition: "all 0.3s ease",
  },
  dotNumber: {
    userSelect: "none",
  },
  resultsBox: {
    textAlign: "center",
    marginTop: "3rem",
  },
  feedbackText: {
    fontSize: "1.25rem",
    margin: "1rem 0",
  },
  reviewBtn: {
    marginTop: "1rem",
    padding: "0.6rem 1.2rem",
    backgroundColor: "#636e72",
    color: "white",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  finishReviewBtn: {
    display: "block",
    margin: "0 auto 1.5rem",
    padding: "0.5rem 1rem",
    fontWeight: "bold",
    backgroundColor: "#fdcb6e",
    color: "#2d3436",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  workingInput: {
    width: "100%",
    borderRadius: "8px",
    border: "1px solid #ccc",
    padding: "0.75rem",
    fontSize: "1rem",
    fontFamily: "'Fredoka', cursive",
    marginTop: "1.25rem",
    marginBottom: "1.25rem",
    backgroundColor: "#fff",
    transition: "background-color 0.3s ease",
  },
};

function CalculatorIcon({ allowed }) {
  const label = allowed ? "Calculator allowed" : "No calculator";

  return (
    <svg role="img" aria-label={label} width={56} height={40} viewBox="0 0 56 40"
      style={{ display: "block" }}>
      <title>{label}</title>
      <rect x="5" y="3" width="27" height="34" rx="4" fill="#f1f5f9" stroke="#334155" strokeWidth="2" />
      <rect x="10" y="8" width="17" height="7" rx="1" fill="#cbd5e1" stroke="#334155" />
      <path d="M11 21h2m5 0h2m5 0h2M11 27h2m5 0h2m5 0h2M11 32h2m5 0h2m5 0h2"
        stroke="#334155" strokeWidth="3" strokeLinecap="round" />
      {allowed ? (
        <path d="M37 22l5 5 9-12" fill="none" stroke="#138a5b" strokeWidth="4"
          strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M5 5l27 30M32 5L5 35" fill="none" stroke="#d64545" strokeWidth="4"
          strokeLinecap="round" />
      )}
    </svg>
  );
}


// Generate short feedback tones locally, without loading audio files.
let quizAudioContext;
async function playQuizFeedback(correct) {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    if (!quizAudioContext || quizAudioContext.state === "closed") quizAudioContext = new AudioContextClass();
    if (quizAudioContext.state === "suspended") await quizAudioContext.resume();
    const context = quizAudioContext;
    const start = context.currentTime;
    const oscillator = context.createOscillator();
    const volume = context.createGain();
    oscillator.type = correct ? "sine" : "triangle";
    oscillator.frequency.setValueAtTime(correct ? 880 : 240, start);
    if (correct) oscillator.frequency.setValueAtTime(1174.66, start + 0.07);
    else {
      oscillator.frequency.exponentialRampToValueAtTime(170, start + 0.1);
      oscillator.frequency.setValueAtTime(210, start + 0.14);
      oscillator.frequency.exponentialRampToValueAtTime(130, start + 0.28);
    }
    const duration = correct ? 0.2 : 0.3;
    volume.gain.setValueAtTime(0.0001, start);
    volume.gain.exponentialRampToValueAtTime(correct ? 0.15 : 0.14, start + 0.008);
    if (!correct) {
      volume.gain.exponentialRampToValueAtTime(0.0001, start + 0.11);
      volume.gain.setValueAtTime(0.0001, start + 0.14);
      volume.gain.exponentialRampToValueAtTime(0.14, start + 0.15);
    }
    volume.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(volume);
    volume.connect(context.destination);
    oscillator.onended = () => { oscillator.disconnect(); volume.disconnect(); };
    oscillator.start(start);
    oscillator.stop(start + duration + 0.01);
  } catch {
    // Audio availability should never interrupt answer checking.
  }
}
